<!--
    The menu to allow different steps to be selected in the discharge / tanker reciept process.

    This is a bit of a mess, I have done a bit to clean it up, but it really ought to be reworked futher at some stage.

    NOTE: THIS IS A LEGACY COMPONENT, ONLY USED FOR THE OLD PRE VOYAGE MANAGEMENT PLANS. SHOULD BE REMOVED WHEN THOSE
    PLANS ARE MIGRATED OR OTHERWISE NOT NEEDED.
-->

<script setup lang="ts">
import { watch, ref, computed } from "vue";
import { storeToRefs } from "pinia";
import { useRouter, useRoute, RouteParamsRaw } from "vue-router";
import { DISPLAY_BREADCRUMB, ROUTE_NAMES } from "@/router/routes";
import { useUiStore } from "@/stores/ui";
import { useTerminalsStore } from "@/stores/terminals";
import { useDischargePlanStoreV1 } from "@/stores/legacy/dischargePlan";
import { PlanSchemaVersion, TankPlanModel } from "@/api/dischargePlan/types";

const router = useRouter();
const route = useRoute();

const uiStore = useUiStore();
const terminalsStore = useTerminalsStore();
const dischargePlanStore = useDischargePlanStoreV1();

const { rail, lastTankId, lastPipelineProduct, lastReceiptPlanId } =
  storeToRefs(uiStore);
const { activeTerminal } = storeToRefs(terminalsStore);
const { activePlan } = storeToRefs(dischargePlanStore);

// This component shows all the routes which are children of this route here.
const routes = ref(
  router.options.routes.filter(
    (t) => t.name === ROUTE_NAMES.TANKER_DISCHARGE,
  )[0].children,
);

// This identifies which route is the current parent.
const routeParent = computed(() => {
  const parent = routes.value
    ?.filter((r) => r.name !== ROUTE_NAMES.RECEIPT_PLAN_LIST)
    .filter((r) =>
      r.children?.find(
        (c) =>
          c.name === route.name ||
          c.children?.find((x) => x.name === route.name),
      ),
    );
  return parent && parent.length > 0
    ? [parent[0].name as string]
    : [ROUTE_NAMES.PRE_ARRIVAL];
});

// Gonna be honest, no clue what this is
const open = ref(rail.value ? [] : routeParent.value);
watch(
  rail,
  (value) => {
    // If the rail is just closed, then
    open.value = !value ? open.value : [routeParent.value[0]];
  },
  { immediate: true },
);

// This says to show this component only if this route is one in the discharge set.
const show = computed(() => {
  const isValidDischargeRoute = routes.value
    ?.filter((r) => r.name !== ROUTE_NAMES.RECEIPT_PLAN_LIST)
    .find(
      (r) =>
        r.name === route.name ||
        r.children?.find(
          (c) =>
            c.name === route.name ||
            c.children?.find((x) => x.name === route.name),
        ),
    );
  return typeof isValidDischargeRoute !== "undefined";
});

// Returns  a list of all the active tanks on the current plan
const activeTanks = computed(() => {
  // If there are no receipt plans, there can be no active tanks.
  if (!activePlan.value?.receiptPlans) {
    return [];
  }

  // Reduce the recipets into just a list of all the tanks
  const receiptPlans = Object.values(activePlan.value.receiptPlans);
  const tankPlans = receiptPlans.reduce(
    (tanks: TankPlanModel[], receiptPlan) =>
      tanks.concat(Object.values(receiptPlan.tankPlans)),
    [],
  );

  // And filter them by the active conditions
  return tankPlans.filter(
    (tank) => !!tank.plannedReceipt || !!tank.pipelineAdjustments,
  );
});

// Returns a list of all the active producs on the current plan
const activeProducts = computed(() => {
  // Get all the products for active tanks
  const activeTankProducts = activeTanks.value.map((tank) => tank.product);

  // make the product list unique
  return activeTankProducts.filter((v, i, s) => s.indexOf(v) === i);
});

/**
 * A helper method which applies neccesary transformations to routes
 * in the list
 * @param path
 * @param name
 */
function handleRoute(path: string) {
  const params: RouteParamsRaw = {
    terminalId: activeTerminal.value?.id,
    planId: route.params.planId,
    product:
      route.params.product ??
      (activeProducts.value.length > 0 ? activeProducts.value[0] : null),
    receiptPlanId:
      route.params.receiptPlanId ??
      (activeTanks.value.length > 0
        ? activeTanks.value[0].receiptPlanId
        : null),
    tankPlanId:
      route.params.tankPlanId ??
      (activeTanks.value.length > 0 ? activeTanks.value[0].id : null),
  };

  path = path
    .replace(":terminalId?", params.terminalId?.toString() ?? "")
    .replace(":terminalId", params.terminalId?.toString() ?? "")
    .replace(":tankPlanId?", params.tankPlanId?.toString() ?? "")
    .replace(":tankPlanId", params.tankPlanId?.toString() ?? "")
    .replace(":receiptPlanId?", params.receiptPlanId?.toString() ?? "")
    .replace(":receiptPlanId", params.receiptPlanId?.toString() ?? "")
    .replace(":planId?", params.planId?.toString() ?? "")
    .replace(":planId", params.planId?.toString() ?? "")
    .replace(":product?", params.product?.toString().toLowerCase() ?? "")
    .replace(":product", params.product?.toString().toLowerCase() ?? "");

  return path;
}

/**
 * Returns true if the route is currently allowed for the state of the plan.
 *
 * @param key An element in the route metadata, which specifies the condition
 */
function isAllowed(key: string) {
  if (!key) {
    return false;
  }

  if (
    key === DISPLAY_BREADCRUMB.PREARRIVAL ||
    key === DISPLAY_BREADCRUMB.ARRIVAL ||
    key === DISPLAY_BREADCRUMB.DISCHARGE ||
    key === DISPLAY_BREADCRUMB.POSTDISCHARGE
  )
    return true;

  if (key === DISPLAY_BREADCRUMB.ACTIVETANKS)
    return activeTanks.value && activeTanks.value.length > 0;

  if (key === DISPLAY_BREADCRUMB.LINESAMPLINGMENU)
    return activeProducts.value && activeProducts.value.length > 0;

  return true;
}

// Keeps track of the routes, and if it changes to be outside of the recipet plan, resets the store values.
watch(
  route,
  (data) => {
    if (!data.meta.receiptPlan) {
      lastTankId.value = null;
      lastPipelineProduct.value = null;
      lastReceiptPlanId.value = null;
    }
  },
  { immediate: true },
);
</script>
<template>
  <v-list v-if="show" v-model:opened="open" expand nav :selected="route.name">
    <template v-for="(item, i) in routes" :key="i">
      <v-list-group
        v-show="
          isAllowed(item.meta?.display as string) &&
          (item.meta?.planSchemaVersion == null ||
            item.meta?.planSchemaVersion == PlanSchemaVersion.Initial)
        "
        no-action
        :value="item.name as string"
        color="orange"
      >
        <template #activator="{ props }">
          <v-list-item
            v-bind="props"
            :prepend-icon="item.meta?.icon as string"
            class="pl-0"
            color="orange"
            @click="rail = false"
          >
            <v-list-item-title class="parent-title">
              {{ item.meta?.title as string }}
            </v-list-item-title>
          </v-list-item>
        </template>
        <template v-for="(subItem, j) in item.children" :key="j">
          <template
            v-if="
              subItem.meta?.planSchemaVersion == null ||
              subItem.meta?.planSchemaVersion == PlanSchemaVersion.Initial
            "
          >
            <v-list-item
              v-if="!subItem.children"
              color="orange"
              :to="handleRoute(item.path + '/' + subItem.path)"
              :disabled="!isAllowed(subItem.meta?.display as string)"
              :value="subItem.name as string"
              :title="subItem.meta?.title as string"
            />
            <v-list-group
              v-else
              :key="`${j}-child`"
              subgroup
              no-action
              color="orange"
              :value="subItem.meta?.name as string"
            >
              <template #activator="{ props }">
                <v-list-item
                  v-bind="props"
                  color="orange"
                  :title="subItem.meta?.title as string"
                  :value="subItem.meta?.title as string"
                />
              </template>
              <template v-for="(subSubItem, k) in subItem.children" :key="k">
                <v-list-item
                  v-if="!subSubItem.children"
                  :to="
                    handleRoute(
                      item.path + '/' + subItem.path + '/' + subSubItem.path,
                    )
                  "
                  :disabled="!isAllowed(subSubItem.meta?.display as string)"
                  color="orange"
                  :value="subSubItem.meta?.title as string"
                  :title="subSubItem.meta?.title as string"
                />
              </template>
            </v-list-group>
          </template>
        </template>
      </v-list-group>
    </template>
  </v-list>
</template>

<style lang="scss" scoped>
:deep(.v-list-item) {
  font-family: "Montserrat", sans-serif;
  font-size: 16px;
  color: white;
}

.parent-title {
  font-weight: bolder !important;
  font-size: 16px !important;
}

:deep(.v-list-item--disabled) {
  color: rgb(94, 94, 94) !important;
}

:deep(.v-list-item--active) {
  color: orange !important;
}
</style>
